/**
 * Purchase Credits Modal
 * Allows artists to purchase feature credits with their wallet balance
 */

import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  FormHelperText,
  Divider,
  SimpleGrid
} from '@chakra-ui/react';
import { useCreditContext } from '../../context/CreditContext';
import { useWalletContext } from '../../context/WalletContext';

// Predefined credit packages
const QUICK_PACKAGES = [
  { credits: 10, label: '10 Credits', description: '10 hours' },
  { credits: 24, label: '24 Credits', description: '1 day' },
  { credits: 72, label: '72 Credits', description: '3 days' },
  { credits: 168, label: '168 Credits', description: '1 week' },
];

const PurchaseCreditsModal = ({ isOpen, onClose }) => {
  const { purchaseCredits, loading } = useCreditContext();
  const { walletSummary, refreshWallet } = useWalletContext();
  const [amount, setAmount] = useState(10);

  // Get real wallet balance from context
  const walletBalance = walletSummary?.available_balance || 0;

  // Calculate cost
  const cost = amount * 1.00;
  const hasEnoughBalance = walletBalance >= cost;
  const remainingBalance = walletBalance - cost;

  // Handle purchase
  const handlePurchase = async () => {
    if (!hasEnoughBalance) return;

    try {
      await purchaseCredits(amount);
      // Refresh wallet to show updated balance immediately
      await refreshWallet(true);
      onClose();
    } catch (error) {
      console.error('Purchase failed:', error);
      // Still refresh wallet in case partial deduction occurred
      await refreshWallet(true);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="secondary.100" color="primary.100">
        <ModalHeader bg="secondary.100" color="dark.900" borderBottom="1px solid" borderColor="secondary.500">
          Purchase Feature Credits
        </ModalHeader>
        <ModalCloseButton color="primary.900" />

        <ModalBody bg="secondary.100" py={6}>
          <VStack spacing={6} align="stretch">
            {/* Quick Packages */}
            <Box>
              <Text fontWeight="semibold" mb={3} color="primary.900">Quick Packages</Text>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                {QUICK_PACKAGES.map((pkg) => (
                  <Box
                    key={pkg.credits}
                    p={4}
                    bg={amount === pkg.credits ? 'secondary.500' : 'dark.500'}
                    border="2px solid"
                    borderColor={amount === pkg.credits ? 'dark.900' : 'secondary.500'}
                    borderRadius="md"
                    cursor="pointer"
                    onClick={() => setAmount(pkg.credits)}
                    _hover={{ borderColor: 'dark.900' }}
                    transition="all 0.2s"
                  >
                    <VStack spacing={1} align="start">
                      <Text fontWeight="bold" color="primary.100">{pkg.label}</Text>
                      <Text fontSize="sm" color="primary.900">{pkg.description}</Text>
                      <Text fontSize="lg" color="dark.900" fontWeight="bold">
                        ${(pkg.credits * 1.00).toFixed(2)}
                      </Text>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            <Divider borderColor="secondary.500" />

            {/* Custom Amount */}
            <FormControl>
              <FormLabel color="primary.900">Or enter a custom amount</FormLabel>
              <NumberInput
                value={amount}
                onChange={(_, value) => setAmount(value)}
                min={1}
                max={1000}
              >
                <NumberInputField
                  bg="secondary.500"
                  border="1px solid"
                  borderColor="secondary.700"
                  color="primary.100"
                  _focus={{ borderColor: 'dark.900' }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper color="primary.900" />
                  <NumberDecrementStepper color="primary.900" />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText color="primary.900">
                Minimum: 1 credit &bull; Maximum: 1000 credits
              </FormHelperText>
            </FormControl>

            <Divider borderColor="secondary.500" />

            {/* Cost Summary */}
            <Box
              bg="dark.500"
              p={4}
              borderRadius="md"
            >
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text fontWeight="semibold" color="primary.900">Credits:</Text>
                  <Text fontWeight="bold" color="primary.100">{amount} credits</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold" color="primary.900">Hours:</Text>
                  <Text color="primary.100">{amount} hours</Text>
                </HStack>
                <Divider borderColor="secondary.500" />
                <HStack justify="space-between">
                  <Text fontWeight="semibold" color="primary.900">Cost:</Text>
                  <Text color="dark.900" fontWeight="bold" fontSize="lg">
                    ${cost.toFixed(2)}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold" color="primary.900">Wallet Balance:</Text>
                  <Text color="primary.100">${walletBalance.toFixed(2)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold" color="primary.900">After Purchase:</Text>
                  <Text
                    color={hasEnoughBalance ? 'green.400' : 'red.400'}
                    fontWeight="bold"
                  >
                    ${hasEnoughBalance ? remainingBalance.toFixed(2) : walletBalance.toFixed(2)}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {/* Insufficient Balance Warning */}
            {!hasEnoughBalance && (
              <Box bg="secondary.500" border="1px solid" borderColor="red.400" borderRadius="md" p={3}>
                <Text fontWeight="bold" color="red.400">Insufficient Wallet Balance</Text>
                <Text fontSize="sm" color="red.300">
                  You need ${(cost - walletBalance).toFixed(2)} more in your wallet.
                  Add funds to your wallet first.
                </Text>
              </Box>
            )}

            {/* Info Box */}
            <Box
              bg="secondary.500"
              p={3}
              borderRadius="md"
              border="1px solid"
              borderColor="dark.900"
            >
              <Text fontSize="sm" color="dark.900">
                <Text as="span" fontWeight="bold">Note:</Text> Purchased credits never expire and will be used
                after your subscription credits.
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter bg="secondary.100" borderTop="1px solid" borderColor="secondary.500">
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="glassy"
            onClick={handlePurchase}
            isLoading={loading}
            isDisabled={!hasEnoughBalance || loading || amount < 1}
          >
            Purchase {amount} Credits
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PurchaseCreditsModal;
